// Ogma Quote: the pricing evaluator (step 9.16; docs/specs/CONNECT.md section 7.1). Written once and used by three
// callers, so a price is worked out the same way everywhere:
//   - the app's test panel (apps/crm/src/lib/quotes.ts imports this file, as it imports intake's whatsapp_note.ts),
//   - the "quote" Edge Function (supabase/functions/quote), which the engine and the quote page call,
//   - the act function, which prices a quote at once when a person confirms its answers or makes one by hand.
// No imports, so the app and Deno can both load it. Fixture tests (supabase/functions/quote/tests/fixtures) run in
// both runtimes and keep them in step.
//
// Pricing is deterministic: a model never sets a price. A price version's definition is plain data:
//   products     what is sold: unit, price (or volume bands) and (optionally) cost, in the business's currency, and how
//                it is billed: one off (the default), monthly or yearly
//   questions    the inputs the price needs: type (number, choice, yes_no, postcode, date, or a repeating group of
//                those), range, where they may come from
//   term         optional: the number question that holds the contract term in months (shown, and usable in steps)
//   tables       rate tables: multi-way lookups from answers to a factor, first matching row wins
//   steps        in order: factors and formula adjustments, then minimums, then (the extras join) discounts, then tax
//   options      up to 5, each with its own name (good, better and best are optional tiers): product lines with
//                quantities (and an optional condition), and what is included
//   extras       optional lines the customer can switch on or off on the quote page
//   guardrails   lowest margin, largest discount, validity in days
//   refer        rules that send a quote to the owner ("over £20k, or outside our area")
//   decline      rules for work the business doesn't quote for
//   samples      sample cases for the test panel
// Formulas are plain arithmetic on the answers (numbers, + - * /, brackets, comparisons, min, max, round, ceil,
// floor, abs, if, and over a repeating group sum(group.field) and count(group)), parsed here into a tree and worked
// out by walking it. Nothing is ever run as code.
//
// Each billing type is priced on its own (Quote module, docs/specs/QUOTE.md): a product's lines go to its billing type,
// proportional steps (factors, multiplying adjustments, discounts, tax) apply to every billing type unless a step names
// one, and fixed amounts (adding adjustments, minimums) go to the step's billing type (needed when there is more than
// one). An option's net, tax and total are one period of each: the one-off charges plus a month plus a year. A book
// with one billing type prices exactly as before.
//
// Money is worked in minor units (pence) and rounded half away from zero after every step, so the same answers
// always give the same figures in every runtime.

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
export type QuestionType = "number" | "choice" | "yes_no" | "postcode" | "date" | "group";
/** The types a repeating group's fields may have (no group inside a group). */
export type FieldType = Exclude<QuestionType, "group">;
export type Source = "form" | "chat" | "email" | "meeting";
export type Tier = "good" | "better" | "best";
export type Billing = "one_off" | "monthly" | "yearly";
export type Scalar = string | number | boolean | null;
/** One item of a repeating group: its fields' answers. */
export type GroupItem = Record<string, Scalar>;
export type Answer = Scalar | GroupItem[];
export type Answers = Record<string, Answer>;

/** Volume bands: the unit price by quantity. all_units prices every unit at the band the quantity falls in;
 *  graduated prices the units in each band at that band's price. A band runs up to and including up_to; the last
 *  band's up_to is null (no end). */
export interface Bands {
  mode: "all_units" | "graduated";
  rows: { up_to: number | null; price: number }[];
}

export interface Product {
  key: string;
  label: string;
  unit?: string;
  /** Required unless the product has bands. */
  price?: number;
  bands?: Bands;
  cost?: number | null;
  billing?: Billing;
  description?: string;
}

export interface Question {
  key: string;
  label: string;
  type: QuestionType;
  required?: boolean;
  min?: number | null;
  max?: number | null;
  choices?: { value: string; label?: string }[];
  sources?: Source[];
  help?: string;
  /** A group's fields. */
  fields?: Question[];
  /** A group's fewest and most items (0 to 20). */
  min_items?: number | null;
  max_items?: number | null;
}

export type Cell = "*" | string | string[] | { min?: number | null; max?: number | null };

export interface RateTable {
  key: string;
  label: string;
  inputs: string[];
  rows: { match: Cell[]; factor: number }[];
  default?: number | null;
}

export type Condition =
  | { q: string; op: ConditionOp; value?: unknown }
  | { all: Condition[] }
  | { any: Condition[] }
  | { not: Condition };

export const CONDITION_OPS = ["eq", "ne", "gt", "gte", "lt", "lte", "in", "not_in", "prefix_in", "not_prefix_in", "present", "missing"] as const;
export type ConditionOp = (typeof CONDITION_OPS)[number];

/** Every step may name the billing type it applies to (see the header). */
export type Step = (
  | { kind: "factor"; label: string; table: string; when?: Condition }
  | { kind: "adjust"; label: string; add?: string; multiply?: string; when?: Condition }
  | { kind: "minimum"; label: string; amount: number; when?: Condition }
  | { kind: "discount"; label: string; pct: string; when?: Condition }
  | { kind: "tax"; label: string; rate_pct: number }
) & { billing?: Billing };

export interface OptionDef {
  key: string;
  label: string;
  /** Optional: good, better or best, each at most once. When every option has one they are shown in that order. */
  tier?: Tier;
  /** A line with a condition is left out when the answers don't meet it. */
  lines: { product: string; quantity: string; when?: Condition }[];
  included?: string[];
  default?: boolean;
}

export interface Extra {
  key: string;
  label: string;
  product: string;
  quantity?: string;
  on?: boolean;
  description?: string;
}

export interface Rule {
  reason: string;
  when: Condition;
}

export interface PriceDefinition {
  currency: string;
  products: Product[];
  questions: Question[];
  /** The number question that holds the contract term, in months. */
  term?: { question: string };
  tables?: RateTable[];
  steps?: Step[];
  options: OptionDef[];
  extras?: Extra[];
  guardrails: { min_margin_pct?: number | null; max_discount_pct?: number | null; validity_days: number };
  refer?: Rule[];
  decline?: Rule[];
  terms?: string;
  samples?: { label: string; answers: Answers }[];
}

export interface Line {
  product: string;
  label: string;
  unit: string | null;
  quantity: number;
  /** The unit price (with graduated bands, the average). */
  unit_price: number;
  amount: number;
  billing: Billing;
}

export interface StepLine {
  kind: Step["kind"] | "extras";
  label: string;
  change: number;
  /** The net of this step's billing type after it. */
  net_after: number;
  billing: Billing;
}

export interface ExtraLine {
  key: string;
  label: string;
  description: string | null;
  quantity: number;
  amount: number;
  on: boolean;
  billing: Billing;
}

/** One billing type's figures for an option: one period (the one-off charges, a month, or a year). */
export interface BillingTotal {
  billing: Billing;
  net: number;
  tax: number;
  total: number;
}

export interface Guardrail {
  key: "min_margin" | "max_discount";
  label: string;
  ok: boolean;
  detail: string;
}

export interface OptionResult {
  key: string;
  label: string;
  tier: Tier | null;
  included: string[];
  lines: Line[];
  steps: StepLine[];
  extras: ExtraLine[];
  /** One period of each billing type added together (see the header). */
  net: number;
  tax: number;
  total: number;
  /** Each billing type the option has, in the order one off, monthly, yearly. */
  billing: BillingTotal[];
  /** With a term and something recurring: the one-off charges plus every month (or year) of the term, with tax. */
  contract_value: number | null;
  cost: number | null;
  margin_pct: number | null;
  discount_pct: number;
  guardrails: Guardrail[];
}

export type Outcome = "priced" | "refer" | "decline" | "needs_answers";

export interface QuoteResult {
  outcome: Outcome;
  /** Answers that are missing or out of range: the quote can't be priced until they are fixed. */
  issues: string[];
  refer: string[];
  decline: string[];
  currency: string;
  options: OptionResult[];
  default_option: string | null;
  guardrails_ok: boolean;
  validity_days: number;
  terms: string | null;
  /** The contract term in months, when the price book has one and it was answered. */
  term_months: number | null;
  /** The answers as used: cleaned (numbers as numbers, postcodes upper case), unknown keys dropped. */
  answers: Answers;
}

export interface EvaluateOptions {
  /** Extras switched on or off by the customer, by key. Extras not named keep their default. */
  extras?: Record<string, boolean>;
}

// ---------------------------------------------------------------------------
// Limits and small helpers
// ---------------------------------------------------------------------------
export const LIMITS = {
  products: 200,
  questions: 50,
  tables: 20,
  rows: 500,
  steps: 40,
  options: 5,
  bands: 20,
  fields: 10,
  items: 20,
  extras: 20,
  rules: 20,
  samples: 50,
  included: 20,
  formula: 300,
  label: 120,
  text: 400,
  terms: 2000,
  money: 10_000_000,
} as const;

export const KEY_RE = /^[a-z][a-z0-9_]{0,39}$/;
const CURRENCY_RE = /^[A-Z]{3}$/;
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const POSTCODE_RE = /^[A-Z0-9][A-Z0-9 -]{1,9}$/;
const TIERS: Tier[] = ["good", "better", "best"];
const FIELD_TYPES: FieldType[] = ["number", "choice", "yes_no", "postcode", "date"];
const TYPES: QuestionType[] = [...FIELD_TYPES, "group"];
const SOURCES: Source[] = ["form", "chat", "email", "meeting"];
export const BILLINGS: Billing[] = ["one_off", "monthly", "yearly"];
/** Fields a rule may test besides the answers. */
export const RESULT_FIELDS = ["total", "net", "margin_pct", "discount_pct"] as const;
/** Names a formula may use besides the answers: the running net before the step. */
const FORMULA_NAMES = ["subtotal"];
const RANK: Record<Step["kind"], number> = { factor: 1, adjust: 1, minimum: 2, discount: 3, tax: 4 };

/** Half away from zero, to a whole number of minor units. */
export function roundMinor(x: number): number {
  const s = x < 0 ? -1 : 1;
  return s * Math.floor(Math.abs(x) + 0.5 + 1e-9);
}

const toMinor = (major: number) => roundMinor(major * 100);
const toMajor = (minor: number) => minor / 100;
const pct1 = (x: number) => Math.round(x * 10) / 10;

export function isObj(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

export function isNum(v: unknown): v is number {
  return typeof v === "number" && Number.isFinite(v);
}

// ---------------------------------------------------------------------------
// Formulas: plain arithmetic, parsed into a tree (never run as code)
// ---------------------------------------------------------------------------
type Node =
  | { t: "num"; v: number }
  | { t: "name"; v: string }
  | { t: "neg"; a: Node }
  | { t: "bin"; op: string; a: Node; b: Node }
  | { t: "call"; f: string; args: Node[] };

const FUNCS: Record<string, [number, number]> = { min: [1, 10], max: [1, 10], round: [1, 2], ceil: [1, 1], floor: [1, 1], abs: [1, 1], if: [3, 3], sum: [1, 1], count: [1, 1] };

/** The numbers a formula is worked out on: answers by key, and for a repeating group its items (count) and each
 * number field as a list ("sites.users"), which only sum() and count() may read. */
export type Env = Record<string, number | number[]>;

export class FormulaError extends Error {}

function tokenise(src: string): string[] {
  const out: string[] = [];
  const re = /\s*(?:(\d+(?:\.\d+)?)|([a-z][a-z0-9_]*(?:\.[a-z][a-z0-9_]*)?)|(<=|>=|==|!=|[-+*/(),<>]))/y;
  let i = 0;
  while (i < src.length) {
    if (/^\s*$/.test(src.slice(i))) break;
    re.lastIndex = i;
    const m = re.exec(src);
    if (!m) throw new FormulaError(`can't read "${src.slice(i, i + 12).trim()}"`);
    out.push(m[1] ?? m[2] ?? m[3]!);
    i = re.lastIndex;
  }
  return out;
}

/** Parses a formula into a tree. Throws FormulaError with a plain message. */
export function parseFormula(src: string): Node {
  if (typeof src !== "string" || !src.trim()) throw new FormulaError("is empty");
  if (src.length > LIMITS.formula) throw new FormulaError(`is longer than ${LIMITS.formula} characters`);
  const toks = tokenise(src);
  let p = 0;
  let depth = 0;
  const peek = () => toks[p];
  const take = (want?: string) => {
    const t = toks[p];
    if (t === undefined || (want !== undefined && t !== want)) throw new FormulaError(want ? `expected "${want}"` : "ends too soon");
    p++;
    return t;
  };
  const deeper = () => {
    if (++depth > 30) throw new FormulaError("is nested too deeply");
  };
  const cmp = (): Node => {
    const a = add();
    const op = peek();
    if (op && ["<", "<=", ">", ">=", "==", "!="].includes(op)) {
      p++;
      return { t: "bin", op, a, b: add() };
    }
    return a;
  };
  const add = (): Node => {
    let a = mul();
    while (peek() === "+" || peek() === "-") {
      const op = take();
      a = { t: "bin", op, a, b: mul() };
    }
    return a;
  };
  const mul = (): Node => {
    let a = unary();
    while (peek() === "*" || peek() === "/") {
      const op = take();
      a = { t: "bin", op, a, b: unary() };
    }
    return a;
  };
  const unary = (): Node => {
    if (peek() === "-") {
      p++;
      deeper();
      const a = unary();
      depth--;
      return { t: "neg", a };
    }
    return primary();
  };
  const primary = (): Node => {
    const t = take();
    if (/^\d/.test(t)) return { t: "num", v: Number(t) };
    if (t === "(") {
      deeper();
      const e = cmp();
      take(")");
      depth--;
      return e;
    }
    if (/^[a-z]/.test(t)) {
      if (peek() === "(") {
        const range = FUNCS[t];
        if (t.includes(".")) throw new FormulaError(`didn't expect "("`);
        if (!range) throw new FormulaError(`"${t}" is not a function you can use (min, max, round, ceil, floor, abs, if, sum, count)`);
        p++;
        deeper();
        const args: Node[] = [];
        if (peek() !== ")") {
          args.push(cmp());
          while (peek() === ",") {
            p++;
            args.push(cmp());
          }
        }
        take(")");
        depth--;
        if (args.length < range[0] || args.length > range[1]) throw new FormulaError(`${t} takes ${range[0] === range[1] ? range[0] : `${range[0]} to ${range[1]}`} values`);
        return { t: "call", f: t, args };
      }
      return { t: "name", v: t };
    }
    throw new FormulaError(`didn't expect "${t}"`);
  };
  const tree = cmp();
  if (p !== toks.length) throw new FormulaError(`didn't expect "${toks[p]}"`);
  checkLists(tree);
  return tree;
}

/** A group's field ("sites.users") is only read whole by sum(), and count() takes a group's key. */
function checkLists(n: Node, inSum = false): void {
  if (n.t === "name") {
    if (n.v.includes(".") && !inSum) throw new FormulaError(`"${n.v}" is a group's field: add it up with sum(${n.v})`);
  } else if (n.t === "neg") checkLists(n.a);
  else if (n.t === "bin") {
    checkLists(n.a);
    checkLists(n.b);
  } else if (n.t === "num") return;
  else if (n.f === "sum") {
    const a = n.args[0]!;
    if (a.t !== "name" || !a.v.includes(".")) throw new FormulaError("sum adds up a group's field, such as sum(sites.users)");
    checkLists(a, true);
  } else if (n.f === "count") {
    const a = n.args[0]!;
    if (a.t !== "name" || a.v.includes(".")) throw new FormulaError("count counts a group's items, such as count(sites)");
  } else n.args.forEach((a) => checkLists(a));
}

function list(n: Node, env: Env): number[] {
  const name = n.t === "name" ? n.v : "?";
  const v = env[name];
  if (!Array.isArray(v)) throw new FormulaError(`needs "${name}", which has no answers`);
  return v;
}

function calc(n: Node, env: Env): number {
  switch (n.t) {
    case "num":
      return n.v;
    case "name": {
      const v = env[n.v];
      if (v === undefined) throw new FormulaError(`needs "${n.v}", which has no number`);
      if (Array.isArray(v)) throw new FormulaError(`"${n.v}" is a group: count its items with count(${n.v})`);
      return v;
    }
    case "neg":
      return -calc(n.a, env);
    case "bin": {
      const a = calc(n.a, env);
      const b = calc(n.b, env);
      switch (n.op) {
        case "+":
          return a + b;
        case "-":
          return a - b;
        case "*":
          return a * b;
        case "/":
          if (b === 0) throw new FormulaError("divides by zero");
          return a / b;
        case "<":
          return a < b ? 1 : 0;
        case "<=":
          return a <= b ? 1 : 0;
        case ">":
          return a > b ? 1 : 0;
        case ">=":
          return a >= b ? 1 : 0;
        case "==":
          return Math.abs(a - b) < 1e-9 ? 1 : 0;
        default:
          return Math.abs(a - b) < 1e-9 ? 0 : 1;
      }
    }
    case "call": {
      if (n.f === "if") return calc(n.args[0]!, env) !== 0 ? calc(n.args[1]!, env) : calc(n.args[2]!, env);
      if (n.f === "sum") return list(n.args[0]!, env).reduce((a, b) => a + b, 0);
      if (n.f === "count") return list(n.args[0]!, env).length;
      const xs = n.args.map((a) => calc(a, env));
      switch (n.f) {
        case "min":
          return Math.min(...xs);
        case "max":
          return Math.max(...xs);
        case "round": {
          const dp = Math.max(0, Math.min(6, Math.trunc(xs[1] ?? 0)));
          const f = 10 ** dp;
          return roundMinor(xs[0]! * f) / f;
        }
        case "ceil":
          return Math.ceil(xs[0]! - 1e-9);
        case "floor":
          return Math.floor(xs[0]! + 1e-9);
        default:
          return Math.abs(xs[0]!);
      }
    }
  }
}

/** Works a formula out against numbers. Throws FormulaError. */
export function evalFormula(src: string, env: Env): number {
  const v = calc(parseFormula(src), env);
  if (!Number.isFinite(v)) throw new FormulaError("does not give a number");
  return v;
}

// ---------------------------------------------------------------------------
// Checking a definition (before it is saved as checked, and always before publishing)
// ---------------------------------------------------------------------------
/** What a formula reads: plain answers (and subtotal), groups' number fields ("sites.users") and counted groups. */
function formulaRefs(n: Node, out = { plain: new Set<string>(), fields: new Set<string>(), counted: new Set<string>() }) {
  if (n.t === "name") (n.v.includes(".") ? out.fields : out.plain).add(n.v);
  else if (n.t === "neg") formulaRefs(n.a, out);
  else if (n.t === "bin") {
    formulaRefs(n.a, out);
    formulaRefs(n.b, out);
  } else if (n.t === "call" && n.f === "count" && n.args[0]?.t === "name") out.counted.add(n.args[0].v);
  else if (n.t === "call") n.args.forEach((a) => formulaRefs(a, out));
  return out;
}

/** What a rule may test: answers by key (groups among them, which only present and missing can test) and, for refer
 * rules, the price's own figures (RESULT_FIELDS). */
export interface ConditionNames {
  answers: Set<string>;
  groups: Set<string>;
  results: boolean;
}

/** Everything wrong with a rule (the Condition grammar), reported through err. Shared by the price book's rules and the
 * question flows' show-if and fit rules (flows.ts). */
export function checkCondition(c: unknown, where: string, names: ConditionNames, err: (m: string) => void, depth = 0): void {
  if (depth > 6) return err(`${where}: the rule is nested too deeply.`);
  if (!isObj(c)) return err(`${where}: the rule must be an object.`);
  if (Array.isArray(c.all) || Array.isArray(c.any)) {
    const xs = (c.all ?? c.any) as unknown[];
    if (xs.length === 0 || xs.length > 20) err(`${where}: "all" and "any" need 1 to 20 parts.`);
    xs.slice(0, 20).forEach((x) => checkCondition(x, where, names, err, depth + 1));
    return;
  }
  if (c.not !== undefined) return checkCondition(c.not, where, names, err, depth + 1);
  const q = c.q;
  if (typeof q !== "string") return err(`${where}: say which answer the rule tests (q).`);
  const isResult = (RESULT_FIELDS as readonly string[]).includes(q);
  if (isResult && !names.results) err(`${where}: "${q}" can't be used here, only answers.`);
  if (!isResult && !names.answers.has(q)) err(`${where}: "${q}" is not one of your questions.`);
  if (!CONDITION_OPS.includes(c.op as ConditionOp)) return err(`${where}: the test must be one of ${CONDITION_OPS.join(", ")}.`);
  const op = c.op as ConditionOp;
  if (op === "present" || op === "missing") return;
  if (names.groups.has(q)) return err(`${where}: a repeating group can only be tested for present or missing.`);
  if (["in", "not_in", "prefix_in", "not_prefix_in"].includes(op)) {
    if (!Array.isArray(c.value) || c.value.length === 0 || c.value.length > 200 || c.value.some((v) => typeof v !== "string" && !isNum(v))) err(`${where}: "${op}" needs a list of 1 to 200 values.`);
    return;
  }
  if (["gt", "gte", "lt", "lte"].includes(op) && !isNum(c.value)) return err(`${where}: "${op}" needs a number.`);
  if (c.value === undefined || (typeof c.value !== "string" && !isNum(c.value) && typeof c.value !== "boolean")) err(`${where}: "${op}" needs a value.`);
}

/** The keys of the answers a rule tests. */
export function conditionKeys(c: Condition, out: Set<string> = new Set()): Set<string> {
  if ("all" in c) c.all.forEach((x) => conditionKeys(x, out));
  else if ("any" in c) c.any.forEach((x) => conditionKeys(x, out));
  else if ("not" in c) conditionKeys(c.not, out);
  else out.add(c.q);
  return out;
}

/** The billing types a definition's products use, in the order one off, monthly, yearly. */
function billingsOf(products: unknown[]): Billing[] {
  const used = new Set(products.map((p) => (isObj(p) && BILLINGS.includes(p.billing as Billing) ? (p.billing as Billing) : "one_off")));
  return BILLINGS.filter((b) => used.has(b));
}

/** Everything wrong with a definition, in plain words. Empty when it can be published. */
export function checkDefinition(raw: unknown): string[] {
  const errs: string[] = [];
  const err = (m: string) => {
    if (errs.length < 50) errs.push(m);
  };
  if (!isObj(raw)) return ["The price book must be an object."];
  const d = raw as Record<string, unknown>;
  if (typeof d.currency !== "string" || !CURRENCY_RE.test(d.currency)) err("Currency must be a three-letter code, such as GBP.");

  const keysOf = (list: unknown, what: string, max: number): Map<string, Record<string, unknown>> => {
    const m = new Map<string, Record<string, unknown>>();
    if (list === undefined) return m;
    if (!Array.isArray(list)) {
      err(`${what} must be a list.`);
      return m;
    }
    if (list.length > max) err(`${what}: at most ${max}.`);
    list.slice(0, max).forEach((x, i) => {
      if (!isObj(x)) return err(`${what} ${i + 1} must be an object.`);
      const k = x.key;
      if (typeof k !== "string" || !KEY_RE.test(k)) return err(`${what} ${i + 1}: the key must be lower case letters, digits and _ (starting with a letter).`);
      if (m.has(k)) return err(`${what}: the key "${k}" is used twice.`);
      if (typeof x.label !== "string" || !x.label.trim() || x.label.length > LIMITS.label) err(`${what} "${k}": the label must be 1 to ${LIMITS.label} characters.`);
      m.set(k, x);
    });
    return m;
  };
  const money = (v: unknown, where: string, allowNull = false) => {
    if (allowNull && (v === null || v === undefined)) return;
    if (!isNum(v) || v < 0 || v > LIMITS.money) err(`${where} must be a number from 0 to ${LIMITS.money.toLocaleString("en-GB")}.`);
  };
  const text = (v: unknown, where: string, max: number = LIMITS.text) => {
    if (v !== undefined && v !== null && (typeof v !== "string" || v.length > max)) err(`${where} must be text of at most ${max} characters.`);
  };

  const products = keysOf(d.products, "Product", LIMITS.products);
  if (!Array.isArray(d.products) || d.products.length === 0) err("Add at least one product or service.");
  for (const [k, p] of products) {
    if (p.bands === undefined) money(p.price, `Product "${k}": the price`);
    else {
      if (p.price !== undefined && p.price !== null) err(`Product "${k}": give a price or volume bands, not both.`);
      const b = p.bands;
      if (!isObj(b) || (b.mode !== "all_units" && b.mode !== "graduated")) err(`Product "${k}": volume bands price all units at one band (all_units) or each band at its own price (graduated).`);
      const rows = isObj(b) && Array.isArray(b.rows) ? b.rows : [];
      if (rows.length < 1 || rows.length > LIMITS.bands) err(`Product "${k}": give 1 to ${LIMITS.bands} volume bands.`);
      let last = 0;
      rows.slice(0, LIMITS.bands).forEach((r, i) => {
        const where = `Product "${k}" band ${i + 1}`;
        if (!isObj(r)) return err(`${where} must be an object.`);
        money(r.price, `${where}: the price`);
        const end = i === rows.length - 1;
        if (end && r.up_to !== null && r.up_to !== undefined) err(`${where}: the last band has no end (up_to empty).`);
        if (!end && (!isNum(r.up_to) || r.up_to <= last)) err(`${where}: each band runs up to a larger number than the one before.`);
        if (isNum(r.up_to)) last = r.up_to;
      });
    }
    money(p.cost, `Product "${k}": the cost`, true);
    if (p.billing !== undefined && !BILLINGS.includes(p.billing as Billing)) err(`Product "${k}": billing is one_off, monthly or yearly.`);
    text(p.unit, `Product "${k}": the unit`, 30);
    text(p.description, `Product "${k}": the description`);
  }
  const billings = billingsOf(Array.isArray(d.products) ? d.products : []);

  const questions = keysOf(d.questions, "Question", LIMITS.questions);
  if (!Array.isArray(d.questions)) err("Questions must be a list (it can be empty).");
  for (const k of [...FORMULA_NAMES, ...RESULT_FIELDS]) if (questions.has(k)) err(`Question "${k}": that key is kept for the price itself; choose another.`);
  const checkQuestion = (k: string, q: Record<string, unknown>, where: string, inGroup: boolean) => {
    const types: readonly string[] = inGroup ? FIELD_TYPES : TYPES;
    if (!types.includes(q.type as string)) err(`${where}: the type must be number, choice, yes_no, postcode, date${inGroup ? "" : " or group"}.`);
    if (q.required !== undefined && typeof q.required !== "boolean") err(`${where}: required must be yes or no.`);
    for (const b of ["min", "max"] as const) if (q[b] !== undefined && q[b] !== null && !isNum(q[b])) err(`${where}: ${b} must be a number.`);
    if (isNum(q.min) && isNum(q.max) && q.min > q.max) err(`${where}: min is above max.`);
    if (q.type === "choice") {
      if (!Array.isArray(q.choices) || q.choices.length < 2 || q.choices.length > 50) err(`${where}: give 2 to 50 choices.`);
      else {
        const seen = new Set<string>();
        for (const c of q.choices) {
          if (!isObj(c) || typeof c.value !== "string" || !c.value.trim() || c.value.length > 60) err(`${where}: each choice needs a value of 1 to 60 characters.`);
          else if (seen.has(c.value)) err(`${where}: the choice "${c.value}" is listed twice.`);
          else seen.add(c.value);
        }
      }
    }
    if (q.sources !== undefined && (!Array.isArray(q.sources) || q.sources.some((s) => !SOURCES.includes(s as Source)))) err(`${where}: sources must be form, chat, email or meeting.`);
    text(q.help, `${where}: the help`);
    if (q.type === "group") {
      const fields = keysOf(q.fields, `Question "${k}" field`, LIMITS.fields);
      if (!Array.isArray(q.fields) || q.fields.length === 0) err(`${where}: a group needs 1 to ${LIMITS.fields} fields.`);
      for (const [fk, f] of fields) checkQuestion(fk, f, `Question "${k}" field "${fk}"`, true);
      for (const b of ["min_items", "max_items"] as const) {
        const v = q[b];
        if (v !== undefined && v !== null && (!isNum(v) || !Number.isInteger(v) || v < 0 || v > LIMITS.items)) err(`${where}: ${b} must be a whole number from 0 to ${LIMITS.items}.`);
      }
      if (isNum(q.min_items) && isNum(q.max_items) && q.min_items > q.max_items) err(`${where}: min_items is above max_items.`);
    }
  };
  for (const [k, q] of questions) checkQuestion(k, q, `Question "${k}"`, false);
  const numeric = new Set([...questions].filter(([, q]) => q.type === "number" || q.type === "yes_no").map(([k]) => k));
  const groups = new Map([...questions].filter(([, q]) => q.type === "group").map(([k, q]) => [k, Array.isArray(q.fields) ? q.fields.filter(isObj) : []]));
  const numericFields = new Set(
    [...groups].flatMap(([g, fields]) => fields.filter((f) => f.type === "number" || f.type === "yes_no").map((f) => `${g}.${String(f.key)}`)),
  );

  const term = d.term;
  if (term !== undefined) {
    if (!isObj(term) || typeof term.question !== "string") err("The term: say which number question holds the term in months (question).");
    else if (questions.get(term.question)?.type !== "number") err(`The term: "${term.question}" must be one of your number questions (months).`);
  }

  const formula = (src: unknown, where: string, extra: string[] = []) => {
    if (typeof src !== "string") return err(`${where} must be a formula, such as "rooms * 2".`);
    try {
      const refs = formulaRefs(parseFormula(src));
      for (const n of refs.plain) {
        if (extra.includes(n) || numeric.has(n)) continue;
        if (groups.has(n)) err(`${where}: "${n}" is a repeating group: use count(${n}) or sum(${n}.field).`);
        else if (questions.has(n)) err(`${where}: "${n}" is not a number or yes/no question, so it can't be used in a formula (use a rate table).`);
        else err(`${where}: "${n}" is not one of your questions.`);
      }
      for (const n of refs.fields) if (!numericFields.has(n)) err(`${where}: "${n}" is not a number or yes/no field of one of your groups.`);
      for (const n of refs.counted) if (!groups.has(n)) err(`${where}: count(${n}) needs one of your repeating groups.`);
    } catch (e) {
      err(`${where}: the formula ${(e as Error).message}.`);
    }
  };

  const condition = (c: unknown, where: string, allowResult: boolean) =>
    checkCondition(c, where, { answers: new Set(questions.keys()), groups: new Set(groups.keys()), results: allowResult }, err);

  const tables = keysOf(d.tables, "Rate table", LIMITS.tables);
  for (const [k, t] of tables) {
    const inputs = Array.isArray(t.inputs) ? (t.inputs as unknown[]) : [];
    if (inputs.length < 1 || inputs.length > 4) err(`Rate table "${k}": it looks up 1 to 4 answers (inputs).`);
    inputs.forEach((n) => {
      if (typeof n !== "string" || !questions.has(n)) err(`Rate table "${k}": "${String(n)}" is not one of your questions.`);
      else if (questions.get(n)!.type === "date") err(`Rate table "${k}": a date question can't be looked up.`);
      else if (questions.get(n)!.type === "group") err(`Rate table "${k}": a repeating group can't be looked up.`);
    });
    if (!Array.isArray(t.rows) || t.rows.length === 0) err(`Rate table "${k}": add at least one row.`);
    else {
      if (t.rows.length > LIMITS.rows) err(`Rate table "${k}": at most ${LIMITS.rows} rows.`);
      t.rows.slice(0, LIMITS.rows).forEach((r, i) => {
        if (!isObj(r) || !Array.isArray(r.match) || r.match.length !== inputs.length) return err(`Rate table "${k}" row ${i + 1}: give one cell for each input.`);
        if (!isNum(r.factor) || r.factor < 0 || r.factor > 1000) err(`Rate table "${k}" row ${i + 1}: the factor must be a number from 0 to 1,000.`);
        r.match.forEach((cell, j) => {
          const q = questions.get(String(inputs[j]));
          if (cell === "*") return;
          if (q?.type === "number") {
            if (!isObj(cell) || (cell.min !== undefined && cell.min !== null && !isNum(cell.min)) || (cell.max !== undefined && cell.max !== null && !isNum(cell.max))) err(`Rate table "${k}" row ${i + 1}: a number cell is "*" or {min, max}.`);
          } else if (!(typeof cell === "string" || (Array.isArray(cell) && cell.length > 0 && cell.every((x) => typeof x === "string")))) {
            err(`Rate table "${k}" row ${i + 1}: a cell is "*", a value, or a list of values.`);
          }
        });
      });
    }
    if (t.default !== undefined && t.default !== null && (!isNum(t.default) || t.default < 0 || t.default > 1000)) err(`Rate table "${k}": the default factor must be a number from 0 to 1,000.`);
  }

  if (d.steps !== undefined && !Array.isArray(d.steps)) err("Pricing steps must be a list.");
  const steps = Array.isArray(d.steps) ? d.steps : [];
  if (steps.length > LIMITS.steps) err(`Pricing steps: at most ${LIMITS.steps}.`);
  let rank = 0;
  const taxes = new Map<string, number>();
  steps.slice(0, LIMITS.steps).forEach((s, i) => {
    const where = `Pricing step ${i + 1}`;
    if (!isObj(s)) return err(`${where} must be an object.`);
    const kind = s.kind as Step["kind"];
    if (!(kind in RANK)) return err(`${where}: the kind must be factor, adjust, minimum, discount or tax.`);
    if (typeof s.label !== "string" || !s.label.trim() || s.label.length > LIMITS.label) err(`${where}: the label must be 1 to ${LIMITS.label} characters.`);
    if (RANK[kind] < rank) err(`${where}: steps go in order: factors and adjustments, then minimums, then discounts, then tax.`);
    rank = Math.max(rank, RANK[kind]);
    if (s.when !== undefined) {
      if (kind === "tax") err(`${where}: tax always applies.`);
      else condition(s.when, where, false);
    }
    if (s.billing !== undefined && !billings.includes(s.billing as Billing)) err(`${where}: billing must be one your products use (${billings.join(", ")}).`);
    const fixed = kind === "minimum" || (kind === "adjust" && s.add !== undefined);
    if (fixed && s.billing === undefined && billings.length > 1) err(`${where}: say which billing it adds to (${billings.join(", ")}), as your products are billed more than one way.`);
    if (kind === "factor" && (typeof s.table !== "string" || !tables.has(s.table))) err(`${where}: "${String(s.table)}" is not one of your rate tables.`);
    if (kind === "adjust") {
      const hasAdd = s.add !== undefined;
      const hasMul = s.multiply !== undefined;
      if (hasAdd === hasMul) err(`${where}: an adjustment either adds or multiplies (one of them).`);
      if (hasAdd) formula(s.add, where, FORMULA_NAMES);
      if (hasMul) formula(s.multiply, where, FORMULA_NAMES);
    }
    if (kind === "minimum") money(s.amount, `${where}: the minimum`);
    if (kind === "discount") formula(s.pct, where, FORMULA_NAMES);
    if (kind === "tax") {
      const on = typeof s.billing === "string" ? s.billing : "every";
      taxes.set(on, (taxes.get(on) ?? 0) + 1);
      if (!isNum(s.rate_pct) || s.rate_pct < 0 || s.rate_pct > 100) err(`${where}: the tax rate must be 0 to 100 per cent.`);
    }
  });
  if ([...taxes.values()].some((n) => n > 1) || (taxes.has("every") && taxes.size > 1)) err("Pricing steps: only one tax step (or one for each billing type).");

  const options = keysOf(d.options, "Option", LIMITS.options);
  if (!Array.isArray(d.options) || d.options.length === 0) err("Add at least one option (good, better or best).");
  const tiers = new Set<string>();
  let defaults = 0;
  for (const [k, o] of options) {
    if (o.tier !== undefined && o.tier !== null) {
      if (!TIERS.includes(o.tier as Tier)) err(`Option "${k}": the tier must be good, better or best.`);
      else if (tiers.has(o.tier as string)) err(`Option "${k}": the tier ${String(o.tier)} is used twice.`);
      else tiers.add(o.tier as string);
    }
    if (o.default === true) defaults++;
    if (!Array.isArray(o.lines) || o.lines.length === 0 || o.lines.length > 30) err(`Option "${k}": give 1 to 30 lines.`);
    else
      o.lines.forEach((l, i) => {
        if (!isObj(l) || typeof l.product !== "string" || !products.has(l.product)) err(`Option "${k}" line ${i + 1}: "${isObj(l) ? String(l.product) : "?"}" is not one of your products.`);
        if (isObj(l)) formula(l.quantity, `Option "${k}" line ${i + 1}: the quantity`);
        if (isObj(l) && l.when !== undefined) condition(l.when, `Option "${k}" line ${i + 1}`, false);
      });
    if (o.included !== undefined && (!Array.isArray(o.included) || o.included.length > LIMITS.included || o.included.some((x) => typeof x !== "string" || !x.trim() || x.length > 200))) err(`Option "${k}": "what is included" is a list of up to ${LIMITS.included} short lines.`);
  }
  if (defaults > 1) err("Only one option can be the default.");

  const extras = keysOf(d.extras, "Extra", LIMITS.extras);
  for (const [k, x] of extras) {
    if (typeof x.product !== "string" || !products.has(x.product)) err(`Extra "${k}": "${String(x.product)}" is not one of your products.`);
    if (x.quantity !== undefined) formula(x.quantity, `Extra "${k}": the quantity`);
    if (x.on !== undefined && typeof x.on !== "boolean") err(`Extra "${k}": "on" must be yes or no.`);
    text(x.description, `Extra "${k}": the description`);
    if (options.has(k)) err(`Extra "${k}": an option has the same key.`);
  }

  const g = d.guardrails;
  if (!isObj(g)) err("Set the guardrails (at least how many days a quote is valid).");
  else {
    if (!isNum(g.validity_days) || !Number.isInteger(g.validity_days) || g.validity_days < 1 || g.validity_days > 365) err("Guardrails: a quote is valid for 1 to 365 days.");
    if (g.min_margin_pct !== undefined && g.min_margin_pct !== null) {
      if (!isNum(g.min_margin_pct) || g.min_margin_pct < -100 || g.min_margin_pct > 100) err("Guardrails: the lowest margin is -100 to 100 per cent.");
      const priced = [...products.values()];
      if (priced.length && priced.every((p) => !isNum(p.cost))) err("Guardrails: a lowest margin needs a cost on your products.");
    }
    if (g.max_discount_pct !== undefined && g.max_discount_pct !== null && (!isNum(g.max_discount_pct) || g.max_discount_pct < 0 || g.max_discount_pct > 100)) err("Guardrails: the largest discount is 0 to 100 per cent.");
  }

  for (const [list, what, allowResult] of [[d.refer, "Refer rule", true], [d.decline, "Decline rule", false]] as const) {
    if (list === undefined) continue;
    if (!Array.isArray(list) || list.length > LIMITS.rules) {
      err(`${what}s: a list of up to ${LIMITS.rules}.`);
      continue;
    }
    list.forEach((r, i) => {
      const where = `${what} ${i + 1}`;
      if (!isObj(r)) return err(`${where} must be an object.`);
      if (typeof r.reason !== "string" || r.reason.trim().length < 3 || r.reason.length > 200) err(`${where}: say why in 3 to 200 characters.`);
      condition(r.when, where, allowResult);
    });
  }
  text(d.terms, "The terms", LIMITS.terms);
  if (d.samples !== undefined) {
    if (!Array.isArray(d.samples) || d.samples.length > LIMITS.samples) err(`Samples: a list of up to ${LIMITS.samples}.`);
    else
      d.samples.forEach((s, i) => {
        if (!isObj(s) || typeof s.label !== "string" || !s.label.trim() || s.label.length > LIMITS.label || !isObj(s.answers)) err(`Sample ${i + 1}: give a label and the answers.`);
      });
  }
  return errs;
}

/** Whether a value is a price book the checker finds nothing wrong with. */
export function isPriceDefinition(raw: unknown): raw is PriceDefinition {
  return checkDefinition(raw).length === 0;
}

// ---------------------------------------------------------------------------
// Answers
// ---------------------------------------------------------------------------
/** One answer cleaned against its question: the value to use, or what is wrong with it. `label` names it in the issue.
 * The question flows (flows.ts) clean these types the same way. */
export function cleanScalar(q: Question, raw: unknown, label: string): { value?: Scalar; issue?: string } {
  const v = typeof raw === "string" ? raw.trim() : raw;
  switch (q.type) {
    case "number": {
      const n = typeof v === "number" ? v : typeof v === "string" && /^-?\d+(\.\d+)?$/.test(v.replace(/,/g, "")) ? Number(v.replace(/,/g, "")) : NaN;
      if (!Number.isFinite(n)) return { issue: `${label}: must be a number.` };
      if (isNum(q.min) && n < q.min) return { issue: `${label}: must be at least ${q.min}.` };
      if (isNum(q.max) && n > q.max) return { issue: `${label}: must be at most ${q.max}.` };
      return { value: n };
    }
    case "yes_no": {
      const b = v === true || v === "yes" || v === "true" ? true : v === false || v === "no" || v === "false" ? false : null;
      return b === null ? { issue: `${label}: must be yes or no.` } : { value: b };
    }
    case "choice": {
      const s = String(v);
      return (q.choices ?? []).some((c) => c.value === s) ? { value: s } : { issue: `${label}: "${s.slice(0, 40)}" is not one of the choices.` };
    }
    case "postcode": {
      const s = String(v).toUpperCase().replace(/\s+/g, " ");
      return POSTCODE_RE.test(s) ? { value: s } : { issue: `${label}: doesn't look like a postcode.` };
    }
    case "date": {
      const s = String(v);
      return DATE_RE.test(s) && !Number.isNaN(Date.parse(`${s}T00:00:00Z`)) ? { value: s } : { issue: `${label}: must be a date (YYYY-MM-DD).` };
    }
    default:
      return { issue: `${label}: can't be answered here.` };
  }
}

export const isEmpty = (v: unknown): boolean => v === undefined || v === null || (typeof v === "string" && v.trim() === "");

/** Cleans the answers against the questions: numbers as numbers, yes/no as true/false, postcodes in capitals, a
 * group as a list of items with their fields cleaned the same way.
 * Returns the cleaned answers and what is wrong (missing required answers, values out of range). */
export function cleanAnswers(def: PriceDefinition, raw: unknown): { answers: Answers; issues: string[] } {
  const given = isObj(raw) ? raw : {};
  const answers: Answers = {};
  const issues: string[] = [];
  for (const q of def.questions ?? []) {
    const v = given[q.key];
    if (isEmpty(v)) {
      if (q.required !== false) issues.push(`${q.label}: needs an answer.`);
      continue;
    }
    if (q.type !== "group") {
      const c = cleanScalar(q, v, q.label);
      if (c.issue) issues.push(c.issue);
      else answers[q.key] = c.value!;
      continue;
    }
    if (!Array.isArray(v) || v.some((x) => !isObj(x))) {
      issues.push(`${q.label}: must be a list.`);
      continue;
    }
    const most = isNum(q.max_items) ? q.max_items : LIMITS.items;
    if (v.length > most) issues.push(`${q.label}: at most ${most}.`);
    else if (isNum(q.min_items) && v.length < q.min_items) issues.push(`${q.label}: at least ${q.min_items}.`);
    else {
      const items: GroupItem[] = [];
      v.forEach((x, i) => {
        const item: GroupItem = {};
        for (const f of q.fields ?? []) {
          const fv = (x as Record<string, unknown>)[f.key];
          const where = `${q.label} ${i + 1}: ${f.label}`;
          if (isEmpty(fv)) {
            if (f.required !== false) issues.push(`${where}: needs an answer.`);
            continue;
          }
          const c = cleanScalar(f, fv, where);
          if (c.issue) issues.push(c.issue);
          else item[f.key] = c.value!;
        }
        items.push(item);
      });
      answers[q.key] = items;
    }
  }
  return { answers, issues };
}

function numbers(def: PriceDefinition, answers: Answers): Env {
  const env: Env = {};
  const num = (q: Question, v: Answer | undefined): number | undefined =>
    q.type === "number" && typeof v === "number" ? v : q.type === "yes_no" && typeof v === "boolean" ? (v ? 1 : 0) : undefined;
  for (const q of def.questions ?? []) {
    const v = answers[q.key];
    if (q.type === "group") {
      const items = Array.isArray(v) ? v : [];
      env[q.key] = items.map((_, i) => i);
      // A missing field counts as nothing in the sum (it was optional).
      for (const f of q.fields ?? []) env[`${q.key}.${f.key}`] = items.map((it) => num(f, it[f.key]) ?? 0);
      continue;
    }
    const n = num(q, v);
    if (n !== undefined) env[q.key] = n;
  }
  return env;
}

// ---------------------------------------------------------------------------
// Rules and tables
// ---------------------------------------------------------------------------
function norm(v: unknown): string {
  return String(v).trim().toUpperCase();
}

/** Whether a rule holds for these answers (and, for refer rules, this option's figures). */
export function holds(c: Condition, answers: Answers, figures: Record<string, number> = {}): boolean {
  if ("all" in c) return c.all.every((x) => holds(x, answers, figures));
  if ("any" in c) return c.any.some((x) => holds(x, answers, figures));
  if ("not" in c) return !holds(c.not, answers, figures);
  const v: unknown = c.q in figures ? figures[c.q] : answers[c.q];
  const has = v !== undefined && v !== null && v !== "" && !(Array.isArray(v) && v.length === 0);
  if (c.op === "present") return has;
  if (c.op === "missing") return !has;
  if (!has || Array.isArray(v)) return false;
  const list = Array.isArray(c.value) ? (c.value as unknown[]) : [];
  switch (c.op) {
    case "eq":
      return typeof v === "number" && isNum(c.value) ? Math.abs(v - c.value) < 1e-9 : norm(v) === norm(c.value);
    case "ne":
      return typeof v === "number" && isNum(c.value) ? Math.abs(v - c.value) >= 1e-9 : norm(v) !== norm(c.value);
    case "gt":
      return typeof v === "number" && v > Number(c.value);
    case "gte":
      return typeof v === "number" && v >= Number(c.value);
    case "lt":
      return typeof v === "number" && v < Number(c.value);
    case "lte":
      return typeof v === "number" && v <= Number(c.value);
    case "in":
      return list.some((x) => norm(x) === norm(v));
    case "not_in":
      return !list.some((x) => norm(x) === norm(v));
    case "prefix_in":
      return list.some((x) => norm(v).startsWith(norm(x)));
    case "not_prefix_in":
      return !list.some((x) => norm(v).startsWith(norm(x)));
    default:
      return false;
  }
}

function cellMatches(cell: Cell, q: Question | undefined, v: Answer): boolean {
  if (cell === "*") return true;
  if (v === undefined || v === null || v === "" || Array.isArray(v)) return false;
  if (q?.type === "number") {
    if (typeof v !== "number" || !isObj(cell)) return false;
    const r = cell as { min?: number | null; max?: number | null };
    return (!isNum(r.min) || v >= r.min) && (!isNum(r.max) || v < r.max);
  }
  const s = typeof v === "boolean" ? (v ? "YES" : "NO") : norm(v);
  const xs = Array.isArray(cell) ? cell : [cell as string];
  if (q?.type === "postcode") return xs.some((x) => s.startsWith(norm(x)));
  return xs.some((x) => norm(x) === s);
}

/** A rate table's factor for these answers: the first matching row, else the default, else null (no rate). */
export function lookup(t: RateTable, def: PriceDefinition, answers: Answers): number | null {
  const qs = new Map((def.questions ?? []).map((q) => [q.key, q]));
  for (const r of t.rows) {
    if (r.match.every((cell, i) => cellMatches(cell, qs.get(t.inputs[i]!), answers[t.inputs[i]!] ?? null))) return r.factor;
  }
  return isNum(t.default) ? t.default : null;
}

/** A line's amount in minor units and its unit price: the product's price, or its volume bands for this quantity. */
export function lineAmount(p: Product, qty: number): { amount: number; unit_price: number } {
  if (!p.bands) return { amount: roundMinor(toMinor(p.price ?? 0) * qty), unit_price: p.price ?? 0 };
  const rows = p.bands.rows;
  if (p.bands.mode === "all_units") {
    const band = rows.find((r) => r.up_to === null || qty <= r.up_to) ?? rows[rows.length - 1]!;
    return { amount: roundMinor(toMinor(band.price) * qty), unit_price: band.price };
  }
  let minor = 0;
  let from = 0;
  for (const r of rows) {
    const to = r.up_to === null ? qty : Math.min(qty, r.up_to);
    if (to > from) minor += toMinor(r.price) * (to - from);
    if (r.up_to === null || qty <= r.up_to) break;
    from = r.up_to;
  }
  const amount = roundMinor(minor);
  return { amount, unit_price: qty > 0 ? Math.round(amount / qty) / 100 : 0 };
}

// ---------------------------------------------------------------------------
// Evaluating
// ---------------------------------------------------------------------------
/**
 * Prices every option for these answers. A definition that fails checkDefinition gives outcome "needs_answers"
 * with the problems as issues; so do answers that are missing or out of range.
 */
export function evaluate(defRaw: unknown, rawAnswers: unknown, opts: EvaluateOptions = {}): QuoteResult {
  const problems = checkDefinition(defRaw);
  const def = defRaw as PriceDefinition;
  const empty = (issues: string[], answers: Answers = {}): QuoteResult => ({
    outcome: "needs_answers",
    issues,
    refer: [],
    decline: [],
    currency: isObj(defRaw) && typeof defRaw.currency === "string" ? defRaw.currency : "GBP",
    options: [],
    default_option: null,
    guardrails_ok: false,
    validity_days: isObj(defRaw) && isObj(defRaw.guardrails) && isNum(defRaw.guardrails.validity_days) ? defRaw.guardrails.validity_days : 30,
    terms: null,
    term_months: null,
    answers,
  });
  if (problems.length) return empty(problems.map((p) => `The price book: ${p}`));
  const { answers, issues } = cleanAnswers(def, rawAnswers);
  if (issues.length) return empty(issues, answers);

  const env = numbers(def, answers);
  const products = new Map(def.products.map((p) => [p.key, p]));
  const tables = new Map((def.tables ?? []).map((t) => [t.key, t]));
  const billingOf = (key: string): Billing => products.get(key)!.billing ?? "one_off";
  const bookBillings = billingsOf(def.products);
  const termAnswer = def.term ? answers[def.term.question] : undefined;
  const termMonths = typeof termAnswer === "number" ? termAnswer : null;
  const refer: string[] = [];
  const decline: string[] = [];
  const formulaIssues: string[] = [];
  const quantity = (src: string | undefined, where: string): number => {
    try {
      const q = evalFormula(src ?? "1", env);
      if (q < 0) {
        formulaIssues.push(`${where}: the quantity comes out below zero.`);
        return 0;
      }
      return Math.round(q * 1000) / 1000;
    } catch (e) {
      formulaIssues.push(`${where}: the quantity ${(e as Error).message}.`);
      return 0;
    }
  };

  for (const r of def.decline ?? []) if (holds(r.when, answers)) decline.push(r.reason);

  const results: OptionResult[] = [];
  for (const o of def.options) {
    // The billing types this option can charge: its lines' and the extras' products (fixed by the definition, so a
    // minimum still applies when every quantity comes out at nothing, as before).
    const own = new Set<Billing>([...o.lines.map((l) => billingOf(l.product)), ...(def.extras ?? []).map((x) => billingOf(x.product))]);
    const buckets = BILLINGS.filter((b) => own.has(b));
    const net = new Map<Billing, number>(buckets.map((b) => [b, 0]));
    const lines: Line[] = [];
    let cost = 0;
    let costKnown = false;
    for (const [i, l] of o.lines.entries()) {
      if (l.when && !holds(l.when, answers)) continue;
      const p = products.get(l.product)!;
      const qty = quantity(l.quantity, `${o.label} line ${i + 1}`);
      if (qty === 0) continue;
      const { amount, unit_price } = lineAmount(p, qty);
      const billing = billingOf(p.key);
      lines.push({ product: p.key, label: p.label, unit: p.unit ?? null, quantity: qty, unit_price, amount: toMajor(amount), billing });
      net.set(billing, net.get(billing)! + toMinor(toMajor(amount)));
      if (isNum(p.cost)) {
        cost += toMinor(p.cost) * qty;
        costKnown = true;
      }
    }
    const steps: StepLine[] = [];
    const beforeDiscount = new Map<Billing, number>();
    let discounted = 0;
    const tax = new Map<Billing, number>();
    const extras: ExtraLine[] = [];
    let extrasDone = false;
    const addExtras = () => {
      if (extrasDone) return;
      extrasDone = true;
      const added = new Map<Billing, number>();
      for (const x of def.extras ?? []) {
        const p = products.get(x.product)!;
        const qty = quantity(x.quantity, `Extra ${x.label}`);
        const { amount } = lineAmount(p, qty);
        const billing = billingOf(p.key);
        const on = opts.extras && x.key in opts.extras ? opts.extras[x.key] === true : x.on === true;
        extras.push({ key: x.key, label: x.label, description: x.description ?? null, quantity: qty, amount: toMajor(amount), on, billing });
        if (on && qty > 0) {
          added.set(billing, (added.get(billing) ?? 0) + amount);
          if (isNum(p.cost)) {
            cost += toMinor(p.cost) * qty;
            costKnown = true;
          }
        }
      }
      for (const b of buckets) {
        const a = added.get(b) ?? 0;
        if (!a) continue;
        net.set(b, net.get(b)! + a);
        steps.push({ kind: "extras", label: "Extras you chose", change: toMajor(a), net_after: toMajor(net.get(b)!), billing: b });
      }
    };
    for (const s of def.steps ?? []) {
      if (RANK[s.kind] >= 3) addExtras();
      if (s.kind !== "tax" && s.when && !holds(s.when, answers)) continue;
      // A fixed amount goes to the step's billing type, or the book's only one; the rest apply to every billing type.
      const fixed = s.kind === "minimum" || (s.kind === "adjust" && s.add !== undefined);
      const targets = s.billing ? buckets.filter((b) => b === s.billing) : fixed ? buckets.filter((b) => b === bookBillings[0]) : buckets;
      if (s.kind === "factor") {
        const f = lookup(tables.get(s.table)!, def, answers);
        if (f === null) {
          refer.push(`No rate in "${tables.get(s.table)!.label}" for these answers.`);
          continue;
        }
      }
      for (const b of targets) {
        const before = net.get(b)!;
        let after = before;
        const stepEnv = { ...env, subtotal: toMajor(before) };
        try {
          if (s.kind === "factor") {
            after = roundMinor(before * lookup(tables.get(s.table)!, def, answers)!);
          } else if (s.kind === "adjust") {
            if (s.add !== undefined) after = roundMinor(before + toMinor(evalFormula(s.add, stepEnv)));
            else after = roundMinor(before * evalFormula(s.multiply!, stepEnv));
          } else if (s.kind === "minimum") {
            after = Math.max(before, toMinor(s.amount));
          } else if (s.kind === "discount") {
            const pct = evalFormula(s.pct, stepEnv);
            if (pct < 0 || pct > 100) {
              formulaIssues.push(`${s.label}: the discount comes out at ${pct}%, outside 0 to 100.`);
              continue;
            }
            if (!beforeDiscount.has(b)) beforeDiscount.set(b, before);
            const off = roundMinor((before * pct) / 100);
            after = before - off;
            discounted += off;
          } else if (s.kind === "tax") {
            const t = roundMinor((before * s.rate_pct) / 100);
            tax.set(b, t);
            // A billing type with nothing in it (an extra left off) shows no tax line; a one-billing book always does.
            if (buckets.length === 1 || before !== 0) steps.push({ kind: "tax", label: s.label, change: toMajor(t), net_after: toMajor(before), billing: b });
            continue;
          }
        } catch (e) {
          formulaIssues.push(`${s.label}: the formula ${(e as Error).message}.`);
          continue;
        }
        net.set(b, after);
        if (after !== before) steps.push({ kind: s.kind, label: s.label, change: toMajor(after - before), net_after: toMajor(after), billing: b });
      }
    }
    addExtras();
    const netAll = buckets.reduce((sum, b) => sum + net.get(b)!, 0);
    const taxAll = buckets.reduce((sum, b) => sum + (tax.get(b) ?? 0), 0);
    if (netAll <= 0) formulaIssues.push(`${o.label}: the price comes out at nothing or less.`);
    const before = [...beforeDiscount.values()].reduce((a, b) => a + b, 0);
    const discountPct = before > 0 ? pct1((discounted / before) * 100) : 0;
    const costMinor = costKnown ? roundMinor(cost) : null;
    const margin = costMinor !== null && netAll > 0 ? pct1(((netAll - costMinor) / netAll) * 100) : null;
    const g = def.guardrails;
    const guardrails: Guardrail[] = [];
    if (isNum(g.min_margin_pct)) {
      guardrails.push(
        margin === null
          ? { key: "min_margin", label: "Lowest margin", ok: false, detail: "No costs, so the margin can't be checked." }
          : { key: "min_margin", label: "Lowest margin", ok: margin >= g.min_margin_pct, detail: `Margin ${margin}% (lowest allowed ${g.min_margin_pct}%).` },
      );
    }
    if (isNum(g.max_discount_pct)) {
      guardrails.push({ key: "max_discount", label: "Largest discount", ok: discountPct <= g.max_discount_pct, detail: `Discount ${discountPct}% (largest allowed ${g.max_discount_pct}%).` });
    }
    const total = netAll + taxAll;
    // A billing type that comes to nothing (an extra left off) is not shown.
    const billing: BillingTotal[] = buckets
      .filter((b) => net.get(b) !== 0 || (tax.get(b) ?? 0) !== 0)
      .map((b) => ({ billing: b, net: toMajor(net.get(b)!), tax: toMajor(tax.get(b) ?? 0), total: toMajor(net.get(b)! + (tax.get(b) ?? 0)) }));
    const periods: Record<Billing, number> = { one_off: 1, monthly: termMonths ?? 0, yearly: (termMonths ?? 0) / 12 };
    const recurring = buckets.some((b) => b !== "one_off");
    const contractValue =
      termMonths !== null && recurring ? toMajor(roundMinor(buckets.reduce((sum, b) => sum + (net.get(b)! + (tax.get(b) ?? 0)) * periods[b], 0))) : null;
    const figures = { total: toMajor(total), net: toMajor(netAll), discount_pct: discountPct, ...(margin !== null ? { margin_pct: margin } : {}) };
    for (const r of def.refer ?? []) if (holds(r.when, answers, figures) && !refer.includes(r.reason)) refer.push(r.reason);
    results.push({
      key: o.key,
      label: o.label,
      tier: o.tier ?? null,
      included: o.included ?? [],
      lines,
      steps,
      extras,
      net: toMajor(netAll),
      tax: toMajor(taxAll),
      total: toMajor(total),
      billing,
      contract_value: contractValue,
      cost: costMinor === null ? null : toMajor(costMinor),
      margin_pct: margin,
      discount_pct: discountPct,
      guardrails,
    });
  }
  // Good, better and best are shown in that order when every option has a tier; otherwise in the book's order.
  if (results.every((r) => r.tier !== null)) results.sort((a, b) => TIERS.indexOf(a.tier!) - TIERS.indexOf(b.tier!));
  const uniqueIssues = [...new Set(formulaIssues)];
  const outcome: Outcome = uniqueIssues.length ? "needs_answers" : decline.length ? "decline" : refer.length ? "refer" : "priced";
  const dflt = def.options.find((o) => o.default)?.key ?? results[0]?.key ?? null;
  return {
    outcome,
    issues: uniqueIssues,
    refer: [...new Set(refer)],
    decline,
    currency: def.currency,
    options: results,
    default_option: dflt,
    guardrails_ok: results.length > 0 && results.every((r) => r.guardrails.every((g) => g.ok)),
    validity_days: def.guardrails.validity_days,
    terms: def.terms?.trim() || null,
    term_months: termMonths,
    answers,
  };
}

// ---------------------------------------------------------------------------
// What the customer sees
// ---------------------------------------------------------------------------
export interface CustomerOption {
  key: string;
  label: string;
  tier: Tier | null;
  included: string[];
  lines: { label: string; unit: string | null; quantity: number; amount: number; billing: Billing }[];
  extras: { key: string; label: string; description: string | null; amount: number; on: boolean; billing: Billing }[];
  net: number;
  tax: number;
  /** The tax step's own words (VAT, insurance premium tax), or null without tax. */
  tax_label: string | null;
  total: number;
  /** Each billing type's figures: "£450 set-up, then £1,240 a month". */
  billing: BillingTotal[];
  contract_value: number | null;
}

/** The quote page's view of a result: no costs, margins, guardrails, rules or pricing steps. */
export function forCustomer(r: QuoteResult): { currency: string; default_option: string | null; terms: string | null; term_months: number | null; options: CustomerOption[] } {
  return {
    currency: r.currency,
    default_option: r.default_option,
    terms: r.terms,
    term_months: r.term_months,
    options: r.options.map((o) => ({
      key: o.key,
      label: o.label,
      tier: o.tier,
      included: o.included,
      lines: o.lines.map((l) => ({ label: l.label, unit: l.unit, quantity: l.quantity, amount: l.amount, billing: l.billing })),
      extras: o.extras.filter((x) => x.quantity > 0).map((x) => ({ key: x.key, label: x.label, description: x.description, amount: x.amount, on: x.on, billing: x.billing })),
      net: o.net,
      tax: o.tax,
      tax_label: o.steps.find((s) => s.kind === "tax")?.label ?? null,
      total: o.total,
      billing: o.billing,
      contract_value: o.contract_value,
    })),
  };
}

/** The words for a billing type's period: "a month", "a year", or "" for one-off charges. */
export const BILLING_PERIOD: Record<Billing, string> = { one_off: "", monthly: "a month", yearly: "a year" };

/** An option's price in words: "£2,016", or "£450 set-up, then £1,240 a month" when it is billed more than one way. */
export function priceLine(billing: BillingTotal[], currency: string, field: "total" | "net" = "total"): string {
  const parts = billing.map((b) => `${formatMoney(b[field], currency)}${b.billing === "one_off" ? (billing.length > 1 ? " set-up" : "") : ` ${BILLING_PERIOD[b.billing]}`}`);
  return parts.length > 1 && billing[0]?.billing === "one_off" ? `${parts[0]}, then ${parts.slice(1).join(" and ")}` : parts.join(" and ");
}

/** Money for people: £1,234.50 (no pence when whole). */
export function formatMoney(amount: number, currency: string): string {
  try {
    const whole = Math.abs(amount % 1) < 1e-9;
    return new Intl.NumberFormat("en-GB", { style: "currency", currency, minimumFractionDigits: whole ? 0 : 2, maximumFractionDigits: 2 }).format(amount);
  } catch {
    return `${currency} ${amount.toFixed(2)}`;
  }
}
